import { useState, useEffect } from 'react';
import {
  usePipelines,
  usePipelineStatuses,
  useCreatePipeline,
  useCreatePipelineStatus,
  useUpdatePipelineStatus,
  useDeletePipelineStatus,
  useReorderPipelineStatuses,
  useSeedDefaultPipeline,
  useUpdatePipeline,
  useDeletePipeline,
} from '@/hooks/usePipelines';
import type { PipelineStatus } from '@/hooks/usePipelines';
import { useProjects } from '@/hooks/useProjects';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ColorPicker } from '@/components/settings/ColorPicker';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2, Check, FolderKanban, Loader2, Pencil, X } from 'lucide-react';

interface SortableStatusRowProps {
  status: PipelineStatus;
  onUpdate: (updates: Partial<Pick<PipelineStatus, 'name' | 'color' | 'is_terminal'>>) => void;
  onDelete: () => void;
}

function SortableStatusRow({ status, onUpdate, onDelete }: SortableStatusRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: status.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [editName, setEditName] = useState(status.name);

  // Keep local input in sync when the server value changes after a successful save
  useEffect(() => {
    setEditName(status.name);
  }, [status.name]);

  const handleNameBlur = () => {
    if (editName.trim() && editName !== status.name) {
      onUpdate({ name: editName.trim() });
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-lg border bg-background p-3"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground hover:text-foreground focus:outline-none"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <div
        className="h-4 w-4 rounded-full flex-shrink-0"
        style={{ backgroundColor: status.color }}
      />

      <Input
        value={editName}
        onChange={e => setEditName(e.target.value)}
        onBlur={handleNameBlur}
        className="h-8 flex-1 text-sm"
      />

      <ColorPicker
        value={status.color}
        onChange={color => onUpdate({ color })}
      />

      <Button
        variant={status.is_terminal ? 'default' : 'outline'}
        size="sm"
        className="h-8 px-2 text-xs gap-1"
        title="Mark as terminal (final) status"
        onClick={() => onUpdate({ is_terminal: !status.is_terminal })}
      >
        <Check className="h-3 w-3" />
        {status.is_terminal ? 'Final' : 'Set Final'}
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-destructive hover:text-destructive"
        onClick={onDelete}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function ProjectPipelines() {
  const { data: pipelines, isLoading: pipelinesLoading } = usePipelines();
  const [activePipelineId, setActivePipelineId] = useState<string | null>(null);

  const selectedPipelineId = activePipelineId ?? pipelines?.[0]?.id ?? null;

  const { data: statuses, isLoading: statusesLoading } = usePipelineStatuses(selectedPipelineId);
  const createPipeline = useCreatePipeline();
  const createStatus = useCreatePipelineStatus();
  const updateStatus = useUpdatePipelineStatus();
  const deleteStatus = useDeletePipelineStatus();
  const reorderStatuses = useReorderPipelineStatuses();
  const seedPipeline = useSeedDefaultPipeline();
  const updatePipeline = useUpdatePipeline();
  const deletePipeline = useDeletePipeline();
  // Projects in the selected pipeline, so the remove confirmation can say what it hides
  const { data: pipelineProjects } = useProjects(selectedPipelineId ?? undefined);

  const [newPipelineName, setNewPipelineName] = useState('');
  const [newStatusName, setNewStatusName] = useState('');
  const [renamingPipeline, setRenamingPipeline] = useState(false);
  const [pipelineNameDraft, setPipelineNameDraft] = useState('');
  const [removeConfirm, setRemoveConfirm] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !statuses || !selectedPipelineId) return;

    const oldIndex = statuses.findIndex(s => s.id === active.id);
    const newIndex = statuses.findIndex(s => s.id === over.id);
    const reordered = arrayMove(statuses, oldIndex, newIndex);

    reorderStatuses.mutate({
      pipelineId: selectedPipelineId,
      orderedIds: reordered.map(s => s.id),
    });
  };

  const handleAddStatus = () => {
    if (!newStatusName.trim() || !selectedPipelineId) return;
    createStatus.mutate({
      pipelineId: selectedPipelineId,
      name: newStatusName.trim(),
      position: statuses?.length ?? 0,
    });
    setNewStatusName('');
  };

  if (pipelinesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Empty state — no pipelines yet
  if (!pipelines || pipelines.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FolderKanban className="h-5 w-5" />
            <CardTitle>Project Pipelines</CardTitle>
          </div>
          <CardDescription>
            Set up pipeline stages to track your installation projects
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4 py-10">
          <p className="text-muted-foreground text-center max-w-sm">
            No pipeline configured yet. Start with the default pipeline or create your own.
          </p>
          <Button
            onClick={() => seedPipeline.mutate()}
            disabled={seedPipeline.isPending}
          >
            {seedPipeline.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Create Default Pipeline
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pipeline selector */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FolderKanban className="h-5 w-5" />
            <CardTitle>Project Pipelines</CardTitle>
          </div>
          <CardDescription>Manage pipeline stages for your installation projects</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Pipeline tabs */}
          <div className="flex flex-wrap gap-2">
            {pipelines.map(p => (
              <Badge
                key={p.id}
                variant={selectedPipelineId === p.id ? 'default' : 'outline'}
                className="cursor-pointer px-3 py-1 text-sm"
                onClick={() => setActivePipelineId(p.id)}
              >
                {p.name}
              </Badge>
            ))}
          </div>

          {/* Add new pipeline */}
          <div className="flex gap-2">
            <Input
              placeholder="New pipeline name..."
              value={newPipelineName}
              onChange={e => setNewPipelineName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && newPipelineName.trim()) {
                  createPipeline.mutate(newPipelineName.trim());
                  setNewPipelineName('');
                }
              }}
              className="max-w-sm"
            />
            <Button
              variant="outline"
              onClick={() => {
                if (newPipelineName.trim()) {
                  createPipeline.mutate(newPipelineName.trim());
                  setNewPipelineName('');
                }
              }}
              disabled={createPipeline.isPending}
            >
              <Plus className="h-4 w-4 mr-1" /> Add Pipeline
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Status management for the selected pipeline */}
      {selectedPipelineId && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              {renamingPipeline ? (
                <form
                  className="flex items-center gap-2"
                  onSubmit={e => {
                    e.preventDefault();
                    const next = pipelineNameDraft.trim();
                    const current = pipelines.find(p => p.id === selectedPipelineId)?.name;
                    if (!next || next === current) { setRenamingPipeline(false); return; }
                    updatePipeline.mutate(
                      { id: selectedPipelineId, name: next },
                      { onSuccess: () => setRenamingPipeline(false) },
                    );
                  }}
                >
                  <Input
                    autoFocus
                    value={pipelineNameDraft}
                    onChange={e => setPipelineNameDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Escape') setRenamingPipeline(false); }}
                    className="h-8 w-64"
                    maxLength={80}
                  />
                  <Button type="submit" size="icon" className="h-8 w-8" disabled={updatePipeline.isPending || !pipelineNameDraft.trim()} title="Save name">
                    {updatePipeline.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  </Button>
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => setRenamingPipeline(false)} title="Cancel">
                    <X className="h-4 w-4" />
                  </Button>
                </form>
              ) : (
                <CardTitle className="text-base">
                  Stages — {pipelines.find(p => p.id === selectedPipelineId)?.name}
                </CardTitle>
              )}
              {!renamingPipeline && (
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setPipelineNameDraft(pipelines.find(p => p.id === selectedPipelineId)?.name ?? '');
                      setRenamingPipeline(true);
                    }}
                  >
                    <Pencil className="h-4 w-4 mr-1" /> Rename
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setRemoveConfirm(true)}
                    disabled={deletePipeline.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete pipeline
                  </Button>
                </div>
              )}
            </div>
            <CardDescription>
              Drag to reorder. Mark a stage as "Final" to indicate project completion.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {statusesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={statuses?.map(s => s.id) ?? []}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {statuses?.map(status => (
                      <SortableStatusRow
                        key={status.id}
                        status={status}
                        onUpdate={updates =>
                          updateStatus.mutate({
                            id: status.id,
                            pipelineId: selectedPipelineId,
                            updates,
                          })
                        }
                        onDelete={() =>
                          deleteStatus.mutate({ id: status.id, pipelineId: selectedPipelineId })
                        }
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Add new status */}
            <div className="flex gap-2 pt-2">
              <Input
                placeholder="New stage name..."
                value={newStatusName}
                onChange={e => setNewStatusName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddStatus()}
                className="max-w-sm"
              />
              <Button
                variant="outline"
                onClick={handleAddStatus}
                disabled={createStatus.isPending || !newStatusName.trim()}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Stage
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete pipeline: permanent. Stages and the projects in it go too (their
          job links with them); the jobs themselves are untouched. */}
      <AlertDialog open={removeConfirm} onOpenChange={setRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete "{pipelines.find(p => p.id === selectedPipelineId)?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pipelineProjects && pipelineProjects.length > 0
                ? `This permanently deletes the pipeline, its stages and the ${pipelineProjects.length} project${pipelineProjects.length === 1 ? '' : 's'} in it. The jobs linked to those projects are not affected. This cannot be undone.`
                : 'This permanently deletes the pipeline and its stages. It has no projects. This cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!selectedPipelineId) return;
                deletePipeline.mutate(selectedPipelineId, {
                  onSuccess: () => { setRemoveConfirm(false); setActivePipelineId(null); },
                });
              }}
            >
              Delete pipeline
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
